// "Email support": a mailto link with a few details filled in, so support can help without a back-and-forth.
import i18n from '@/lib/i18n';
import { track } from '@/lib/analytics';
import { SUPPORT_EMAIL } from '@/features/settings/ui';
import { APP_BUILD } from './open';

function device(): string {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.exec(ua)?.[0];
  const v = /OS (\d+)[._](\d+)/.exec(ua);
  if (ios) return `${ios}, iOS ${v ? `${v[1]}.${v[2]}` : '?'}`;
  const android = /Android [\d.]+/.exec(ua)?.[0];
  if (android) return android;
  return /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Other';
}

export const SUPPORT_ADDRESS = SUPPORT_EMAIL;

function draft(opts: { email?: string | null; extra?: string }) {
  const t = i18n.getFixedT(null, 'help');
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const lines = [
    t('email.details'),
    opts.email ? `Account: ${opts.email}` : null,
    `Screen: ${location.pathname}`,
    `Device: ${device()}${standalone ? ', Home Screen app' : ', browser'}`,
    `App: ${APP_BUILD}`,
    opts.extra ?? null,
  ].filter(Boolean);
  return { subject: t('email.subject'), body: `${t('email.intro')}---\n${lines.join('\n')}` };
}

/** One-tap compose links: Gmail, Outlook, Yahoo Mail in the browser, and the phone's mail app (mailto). */
export function composeLinks(opts: { email?: string | null; extra?: string } = {}): { id: 'gmail' | 'outlook' | 'yahoo' | 'app'; href: string }[] {
  const { subject, body } = draft(opts);
  const to = encodeURIComponent(SUPPORT_EMAIL);
  const su = encodeURIComponent(subject);
  const b = encodeURIComponent(body);
  return [
    { id: 'gmail', href: `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${su}&body=${b}` },
    { id: 'outlook', href: `https://outlook.live.com/mail/0/deeplink/compose?to=${to}&subject=${su}&body=${b}` },
    { id: 'yahoo', href: `https://compose.mail.yahoo.com/?to=${to}&subject=${su}&body=${b}` },
    { id: 'app', href: `mailto:${SUPPORT_EMAIL}?subject=${su}&body=${b}` },
  ];
}

export function supportMailto(opts: { email?: string | null; extra?: string } = {}): string {
  const t = i18n.getFixedT(null, 'help');
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const lines = [
    t('email.details'),
    opts.email ? `Account: ${opts.email}` : null,
    `Screen: ${location.pathname}`,
    `Device: ${device()}${standalone ? ', Home Screen app' : ', browser'}`,
    `App: ${APP_BUILD}`,
    opts.extra ?? null,
  ].filter(Boolean);
  const body = `${t('email.intro')}---\n${lines.join('\n')}`;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('email.subject'))}&body=${encodeURIComponent(body)}`;
}

export function trackEmailClick(source: string) {
  track('support_email_click', { source, path: location.pathname }, 'help');
}
